import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Building2, Users, FileText, MessageSquare, UserCheck, Trash2, Inbox, MapPin, CheckCircle2, XCircle, MessageCircle, Sparkles } from "lucide-react";

const TABS = [
  { id: "orgs", label: "Organizations", icon: Building2 },
  { id: "upgrades", label: "Upgrade requests", icon: Inbox },
  { id: "sites", label: "Workspaces", icon: MapPin },
];

export default function SuperAdmin() {
  const [tab, setTab] = useState("orgs");
  const [orgs, setOrgs] = useState([]);
  const [global, setGlobal] = useState(null);
  const [show, setShow] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: "", plan: "starter", industry: "Biotech" });
  const [retentionBusy, setRetentionBusy] = useState(false);
  const [impersonateOrg, setImpersonateOrg] = useState(null);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/admin/orgs"), api.get("/admin/global-analytics")]);
      setOrgs(a.data); setGlobal(b.data);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/admin/orgs", newOrg);
    toast.success("Org created"); setShow(false); setNewOrg({ name: "", plan: "starter", industry: "Biotech" });
    load();
  };

  const openImpersonate = (org) => setImpersonateOrg(org);

  const impersonateUser = async (user) => {
    try {
      await api.post(`/admin/impersonate/${user.user_id}`);
      toast.success(`Now impersonating ${user.email}`);
      setImpersonateOrg(null);
      if (checkAuth) await checkAuth();
      navigate("/app/chat");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonation failed");
    }
  };

  const runRetention = async () => {
    if (!confirm("Run data-retention sweep now? Conversations, feedback, audit logs and analytics older than each org's configured window will be deleted.")) return;
    setRetentionBusy(true);
    try {
      const { data } = await api.post("/admin/retention/run");
      toast.success(
        `Swept ${data.orgs_processed} orgs — ${data.conversations_deleted} convs, ${data.feedback_deleted ?? 0} feedback, ${data.audit_deleted ?? 0} audit, ${data.events_deleted} events`,
      );
    } catch { toast.error("Retention sweep failed"); }
    finally { setRetentionBusy(false); }
  };

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="font-serif text-3xl mb-1">Super Admin</h1>
            <p className="text-sm text-[var(--aria-text-muted)]">Cross-org operations, upgrade requests and site provisioning.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={runRetention} data-testid="run-retention-btn" disabled={retentionBusy}
              className="flex items-center gap-2 px-4 py-2 rounded-md border border-[var(--aria-border)] text-sm font-medium hover:bg-slate-50 dark:hover:bg-white/5 disabled:opacity-50">
              <Trash2 size={14} /> {retentionBusy ? "Sweeping…" : "Run retention sweep"}
            </button>
            <button onClick={()=>setShow(true)} data-testid="create-org-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#2563FF] text-white text-sm font-medium hover:bg-[#0B3BCE]">
              <Plus size={14} /> Create org
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            {icon: Building2, label: "Organizations", v: global?.orgs},
            {icon: Users, label: "Total users", v: global?.users},
            {icon: FileText, label: "Total docs", v: global?.documents},
            {icon: MessageSquare, label: "Total questions", v: global?.questions},
          ].map((s, i) => (
            <div key={i} className="aria-card p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--aria-text-muted)] mb-3">
                <s.icon size={14} className="text-[#2563FF]" /> {s.label}
              </div>
              <div className="font-serif text-3xl">{s.v ?? "—"}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-4 border-b border-[var(--aria-border)]">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? "border-[#2563FF] text-[#2563FF]"
                  : "border-transparent text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"
              }`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "orgs" && (
          <OrgsTable orgs={orgs} openImpersonate={openImpersonate} />
        )}
        {tab === "upgrades" && (
          <UpgradeRequestsPanel onChange={load} />
        )}
        {tab === "sites" && (
          <SitesPanel onProvisioned={load} />
        )}
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={()=>setShow(false)}>
          <form onSubmit={create} onClick={e=>e.stopPropagation()} className="aria-card p-6 max-w-md w-full">
            <h3 className="font-serif text-xl mb-4">Create organisation</h3>
            <div className="space-y-3">
              <input required placeholder="Name" value={newOrg.name} onChange={e=>setNewOrg({...newOrg, name:e.target.value})}
                data-testid="new-org-name"
                className="w-full px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)]" />
              <select value={newOrg.plan} onChange={e=>setNewOrg({...newOrg, plan:e.target.value})}
                className="w-full px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)]">
                <option value="starter">Starter</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option>
              </select>
              <select value={newOrg.industry} onChange={e=>setNewOrg({...newOrg, industry:e.target.value})}
                className="w-full px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)]">
                {["Biotech","Pharma","Clinical","Research"].map(x => <option key={x}>{x}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={()=>setShow(false)} className="px-4 py-2 text-sm rounded-md border border-[var(--aria-border)]">Cancel</button>
              <button type="submit" data-testid="new-org-submit" className="px-4 py-2 text-sm rounded-md bg-[#2563FF] text-white">Create</button>
            </div>
          </form>
        </div>
      )}
      {impersonateOrg && (
        <ImpersonatePicker
          org={impersonateOrg}
          onClose={() => setImpersonateOrg(null)}
          onPick={impersonateUser}
        />
      )}
    </div>
  );
}

function ImpersonatePicker({ org, onClose, onPick }) {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/users", { params: { org_id: org.org_id } });
        if (!cancelled) setUsers(data || []);
      } catch {
        if (!cancelled) { setUsers([]); toast.error("Failed to load users"); }
      }
    })();
    return () => { cancelled = true; };
  }, [org.org_id]);

  const filtered = (users || []).filter(u => {
    if (!q) return true;
    const s = q.toLowerCase();
    return u.email?.toLowerCase().includes(s) || u.name?.toLowerCase().includes(s);
  });

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/55 backdrop-blur-[3px] flex items-start justify-center pt-24 fade-in"
      onClick={onClose}
      data-testid="impersonate-picker-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="impersonate-picker"
        className="w-full max-w-xl bg-[var(--aria-surface)] border border-[var(--aria-border)] rounded-md shadow-[0_20px_80px_rgba(229,0,104,0.25)] overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-[var(--aria-border)]">
          <div className="text-[10px] font-mono tracking-[0.28em] text-[var(--aria-primary)] uppercase mb-1">
            ◆ Super Admin · Impersonate
          </div>
          <h3 className="font-display text-xl tracking-tight">
            Enter <span className="text-[var(--aria-primary)]">{org.name}</span> as…
          </h3>
          <p className="text-xs text-[var(--aria-text-muted)] mt-1">
            An audit entry will be written. Click a user to enter the session — return to /app/superadmin to release.
          </p>
        </div>
        <div className="p-4 border-b border-[var(--aria-border)]">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            placeholder="Filter by name or email…"
            data-testid="impersonate-filter"
            className="w-full px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] text-sm outline-none focus:ring-2 focus:ring-[var(--aria-primary)]"
          />
        </div>
        <div className="max-h-[420px] overflow-y-auto" data-testid="impersonate-user-list">
          {users === null && (
            <div className="px-5 py-8 text-sm text-[var(--aria-text-muted)] italic">Loading users…</div>
          )}
          {users && filtered.length === 0 && (
            <div className="px-5 py-8 text-sm text-[var(--aria-text-muted)] italic">No matching users.</div>
          )}
          {filtered.map((u) => (
            <button
              key={u.user_id}
              onClick={() => onPick(u)}
              data-testid={`impersonate-user-${u.user_id}`}
              disabled={u.role === "super_admin"}
              title={u.role === "super_admin" ? "Cannot impersonate another super admin" : `Enter as ${u.email}`}
              className="w-full text-left px-5 py-3 border-b border-[var(--aria-border)] last:border-b-0 hover:bg-[var(--aria-surface-2)] transition-colors flex items-center justify-between gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{u.name || u.email}</div>
                <div className="text-xs text-[var(--aria-text-muted)] truncate">{u.email}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 border border-[var(--aria-border)] rounded-sm text-[var(--aria-text-muted)]">
                  {u.role || "user"}
                </span>
                <UserCheck size={14} className="text-[var(--aria-accent)]" />
              </div>
            </button>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-[var(--aria-border)] flex justify-end">
          <button
            onClick={onClose}
            data-testid="impersonate-cancel"
            className="px-3 py-1.5 text-xs font-mono uppercase tracking-[0.18em] border border-[var(--aria-border)] hover:border-[var(--aria-text)] rounded-sm"
          >
            cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function OrgsTable({ orgs, openImpersonate }) {
  return (
    <div className="aria-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#2563FF]/5 text-left text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">
          <tr>
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Plan</th>
            <th className="px-4 py-3">Industry</th>
            <th className="px-4 py-3">Users</th>
            <th className="px-4 py-3">Docs</th>
            <th className="px-4 py-3">Questions</th>
            <th className="px-4 py-3">Created</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {orgs.map(o => (
            <tr key={o.org_id} className="border-t border-[var(--aria-border)]" data-testid={`org-row-${o.org_id}`}>
              <td className="px-4 py-3 font-medium">{o.name}</td>
              <td className="px-4 py-3"><span className="px-2 py-0.5 text-[10px] rounded uppercase tracking-wider bg-[#2563FF]/10 text-[#2563FF]">{o.plan}</span></td>
              <td className="px-4 py-3 text-[var(--aria-text-muted)]">{o.industry}</td>
              <td className="px-4 py-3">{o.users_count}</td>
              <td className="px-4 py-3">{o.documents_count}</td>
              <td className="px-4 py-3">{o.questions_count}</td>
              <td className="px-4 py-3 text-xs text-[var(--aria-text-muted)]">{new Date(o.created_at).toLocaleDateString()}</td>
              <td className="px-4 py-3 text-right">
                <button onClick={() => openImpersonate(o)} data-testid={`impersonate-${o.org_id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded border border-[var(--aria-border)] hover:border-amber-500 hover:text-amber-600 transition-colors">
                  <UserCheck size={12} /> Impersonate
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UpgradeRequestsPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/admin/upgrade-requests", { params });
      setItems(data);
    } catch { toast.error("Failed to load upgrade requests"); }
  };
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [filter]);

  const act = async (req, status, opts = {}) => {
    setBusyId(req.request_id);
    try {
      const body = { status };
      if (opts.note) body.note = opts.note;
      if (status === "approved") body.apply_plan = opts.apply_plan !== false;
      const { data } = await api.put(`/admin/upgrade-requests/${encodeURIComponent(req.request_id)}`, body);
      if (data.plan_changed_to) {
        toast.success(`Approved — ${req.org_name_current || req.org_name} upgraded to ${data.plan_changed_to}`);
      } else {
        toast.success(`Marked ${status}`);
      }
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusyId(null); }
  };

  const STATUS_BADGE = {
    pending:   "bg-amber-500/15 text-amber-600 border-amber-500/30",
    contacted: "bg-sky-500/15 text-sky-600 border-sky-500/30",
    approved:  "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
    rejected:  "bg-rose-500/15 text-rose-600 border-rose-500/30",
  };

  return (
    <div className="aria-card overflow-hidden" data-testid="upgrade-requests-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--aria-border)]">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Inbox size={14} className="text-[#2563FF]" /> Upgrade requests inbox
          <span className="text-[11px] text-[var(--aria-text-muted)] font-normal italic ml-1">
            · actions fire `upgrade.contacted`, `upgrade.approved`, `upgrade.rejected` webhooks on the requesting org
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs">
          {["pending", "contacted", "approved", "rejected", "all"].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-2.5 py-1 rounded-md font-medium ${
                filter === f
                  ? "bg-[#2563FF] text-white"
                  : "text-[var(--aria-text-muted)] hover:bg-[var(--aria-surface-2)]"
              }`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="p-10 text-center text-sm text-[var(--aria-text-muted)]">
          No {filter !== "all" ? filter : ""} upgrade requests. When customers click "Request Upgrade" in Org Settings, they will appear here.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-[#2563FF]/5 text-left text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">
            <tr>
              <th className="px-4 py-3">Org</th>
              <th className="px-4 py-3">Requester</th>
              <th className="px-4 py-3">From → To</th>
              <th className="px-4 py-3">Submitted</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(r => (
              <tr key={r.request_id} className="border-t border-[var(--aria-border)] align-top" data-testid={`upgrade-row-${r.request_id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.org_name_current || r.org_name}</div>
                  {r.notes && (
                    <div className="text-xs text-[var(--aria-text-muted)] mt-1 max-w-md italic">"{r.notes}"</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm">{r.requested_by_name || "—"}</div>
                  <div className="text-xs text-[var(--aria-text-muted)] font-mono">{r.requested_by}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono text-xs">{r.org_plan_current || r.current_plan}</span>
                  <span className="mx-1.5 text-[var(--aria-text-muted)]">→</span>
                  <span className="font-mono text-xs font-semibold text-[#2563FF]">{r.target_plan}</span>
                </td>
                <td className="px-4 py-3 text-xs text-[var(--aria-text-muted)]">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-[10px] rounded uppercase tracking-wider border ${STATUS_BADGE[r.status] || ""}`}>{r.status}</span>
                  {r.action_note && (
                    <div className="text-[11px] text-[var(--aria-text-muted)] mt-1 max-w-[200px]">{r.action_note}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {r.status === "pending" || r.status === "contacted" ? (
                    <div className="flex items-center gap-1 justify-end flex-wrap">
                      {r.status === "pending" && (
                        <button
                          onClick={() => {
                            const note = prompt("Note (optional) — e.g. 'emailed Anna, scheduled call Thu'");
                            if (note === null) return;
                            act(r, "contacted", { note });
                          }}
                          data-testid={`upgrade-contact-${r.request_id}`}
                          disabled={busyId === r.request_id}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-[var(--aria-border)] hover:border-sky-500 hover:text-sky-600 disabled:opacity-50 transition-colors">
                          <MessageCircle size={12} /> Mark contacted
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (!confirm(`Approve and upgrade ${r.org_name_current || r.org_name} to ${r.target_plan}?`)) return;
                          act(r, "approved", { apply_plan: true });
                        }}
                        data-testid={`upgrade-approve-${r.request_id}`}
                        disabled={busyId === r.request_id}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded bg-[#2563FF] text-white hover:bg-[#0B3BCE] disabled:opacity-50 transition-colors">
                        <CheckCircle2 size={12} /> Approve &amp; upgrade
                      </button>
                      <button
                        onClick={() => {
                          const note = prompt("Reason for rejection (optional)");
                          if (note === null) return;
                          act(r, "rejected", { note });
                        }}
                        data-testid={`upgrade-reject-${r.request_id}`}
                        disabled={busyId === r.request_id}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-[var(--aria-border)] hover:border-rose-500 hover:text-rose-600 disabled:opacity-50 transition-colors">
                        <XCircle size={12} /> Reject
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-[var(--aria-text-muted)]">
                      by {r.actioned_by || "—"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SitesPanel({ onProvisioned }) {
  const [presets, setPresets] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [busySite, setBusySite] = useState(null);
  const [adminEmail, setAdminEmail] = useState("");
  const [credentials, setCredentials] = useState(null);

  const load = async () => {
    try {
      const [p, o] = await Promise.all([
        api.get("/admin/site-presets"),
        api.get("/admin/orgs"),
      ]);
      setPresets(p.data);
      setOrgs(o.data);
    } catch { toast.error("Failed to load site presets"); }
  };
  useEffect(() => { load(); }, []);

  const provision = async (site) => {
    setBusySite(site);
    setCredentials(null);
    try {
      const body = { site, plan: "enterprise" };
      if (adminEmail && adminEmail.includes("@")) body.admin_email = adminEmail.trim();
      const { data } = await api.post("/admin/provision-site", body);
      if (data.created) {
        toast.success(`Provisioned workspace ${site}`);
        if (data.admin) setCredentials({ site, ...data.admin });
      } else {
        toast.message(`Workspace ${site} is already provisioned`);
      }
      setAdminEmail("");
      await load();
      onProvisioned && onProvisioned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Provisioning failed");
    } finally { setBusySite(null); }
  };

  const existingByName = new Map(orgs.map(o => [o.name, o]));

  return (
    <div className="space-y-5" data-testid="sites-panel">
      <div className="aria-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium mb-1">
              <Sparkles size={14} className="text-[var(--aria-accent)]" /> Workspace provisioning
            </div>
            <p className="text-xs text-[var(--aria-text-muted)] max-w-2xl leading-relaxed">
              One-click provisioning for additional ARIA workspaces. Each becomes its own isolated tenant
              with its own documents, audit log and (optionally) an org-admin user. Add an admin email below
              before clicking a workspace to bootstrap with a temporary password.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={adminEmail}
              onChange={e => setAdminEmail(e.target.value)}
              placeholder="site-admin@example.com (optional)"
              data-testid="site-admin-email"
              className="px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] text-sm w-72"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {presets.map(p => {
          const orgName = `Workspace ${p.site}`;
          const existing = existingByName.get(orgName);
          return (
            <div key={p.site} className="aria-card p-5 flex flex-col" data-testid={`site-card-${p.site}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <MapPin size={14} className="text-[#2563FF]" />
                    <h3 className="font-serif text-lg">{p.site}</h3>
                  </div>
                  <div className="text-xs text-[var(--aria-text-muted)] mt-1 font-mono">{p.region} · {p.domain}</div>
                </div>
                {existing && (
                  <span className="px-2 py-0.5 text-[10px] rounded uppercase tracking-wider bg-emerald-500/15 text-emerald-600 border border-emerald-500/30">
                    Live
                  </span>
                )}
              </div>
              {existing ? (
                <div className="text-xs text-[var(--aria-text-muted)] mb-3">
                  {existing.users_count} users · {existing.documents_count} docs · plan&nbsp;
                  <span className="font-mono text-[#2563FF]">{existing.plan}</span>
                </div>
              ) : (
                <div className="text-xs text-[var(--aria-text-muted)] mb-3 italic">Not yet provisioned</div>
              )}
              <button
                onClick={() => provision(p.site)}
                disabled={!!existing || busySite === p.site}
                data-testid={`provision-${p.site}`}
                className="mt-auto w-full px-3 py-2 rounded-md bg-[#2563FF] text-white text-sm font-medium hover:bg-[#0B3BCE] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {existing ? "Already provisioned" : (busySite === p.site ? "Provisioning…" : "Provision site")}
              </button>
            </div>
          );
        })}
      </div>

      {credentials && (
        <div className="aria-card p-5 border-amber-500/40 bg-amber-500/5" data-testid="provisioned-credentials">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-medium mb-2">Hand off these credentials to the {credentials.site} site admin</div>
              <div className="text-xs text-[var(--aria-text-muted)] mb-2">Shown once. Copy now — it cannot be retrieved later.</div>
              <div className="font-mono text-sm space-y-1">
                <div>email: <span className="text-[#2563FF]">{credentials.email}</span></div>
                <div>temp password: <span className="text-[#2563FF]">{credentials.temp_password}</span></div>
              </div>
            </div>
            <button onClick={() => setCredentials(null)} className="text-xs underline">Dismiss</button>
          </div>
        </div>
      )}
    </div>
  );
}
